/**
 * Letterhead Field Editor — visual drag-and-drop field placement.
 *
 * Layout:
 *   ┌───────────┬──────────────────────────────┬───────────────┐
 *   │  Field    │   Letterhead canvas (bg =    │   Properties  │
 *   │  library  │   uploaded PDF/image, over-  │   panel       │
 *   │  (left)   │   layed with draggable and   │   (right)     │
 *   │           │   resizable field boxes)     │               │
 *   └───────────┴──────────────────────────────┴───────────────┘
 *
 * Coordinate model: everything stored in millimetres relative to the
 * letterhead's own page geometry (from PDF /MediaBox or image aspect).
 * The canvas is rendered at a chosen pixel-per-mm ratio (zoom); mouse
 * events convert px → mm on the fly so the same template renders exactly
 * on any screen and any server-side DPI.
 *
 * Existing systems reused: `/api/letterheads/{id}` + `PUT
 * /api/letterheads/{id}/fields`. This page NEVER re-renders the uploaded
 * letterhead — the raw file is placed behind the overlays as-is.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Save, Trash2, Copy, Eye, EyeOff, Plus, Loader2, X,
  ZoomIn, ZoomOut, RotateCcw, User, Calendar, Stethoscope, Building2,
  ClipboardList, PenTool, Type, CheckSquare, Table as TableIcon, ImageIcon,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { apiError } from "../../lib/apiError";

const backendBase = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// ---------------------------------------------------------------------
// Field catalog — the source of truth for what the library exposes.
// Every entry maps a display label to a backend field type + default box.
// ---------------------------------------------------------------------
const CATALOG = [
  { group: "Patient",   icon: User, items: [
    { type: "patient_name",       label: "Patient Name",   w: 90, h: 8, font: 11, source: "patient.name" },
    { type: "patient_id",         label: "Patient ID",     w: 40, h: 7, font: 10, source: "patient.id" },
    { type: "patient_age_gender", label: "Age / Gender",   w: 40, h: 7, font: 10, source: "patient.age_gender" },
    { type: "patient_phone",      label: "Phone",          w: 40, h: 7, font: 10, source: "patient.phone" },
    { type: "patient_dob",        label: "Date of Birth",  w: 40, h: 7, font: 10, source: "patient.dob" },
    { type: "patient_address",    label: "Address",        w: 90, h: 12, font: 10, source: "patient.address" },
    { type: "patient_blood_group",label: "Blood Group",    w: 30, h: 7, font: 10, source: "patient.blood_group" },
  ]},
  { group: "Appointment", icon: Calendar, items: [
    { type: "current_date",       label: "Today's Date",   w: 40, h: 7, font: 10, source: "system.today" },
    { type: "appointment_date",   label: "Appointment Date",w: 40, h: 7, font: 10, source: "appointment.date" },
    { type: "appointment_time",   label: "Appointment Time",w: 40, h: 7, font: 10, source: "appointment.time" },
    { type: "follow_up_date",     label: "Follow-up Date", w: 40, h: 7, font: 10, source: "rx.follow_up" },
  ]},
  { group: "Doctor",    icon: Stethoscope, items: [
    { type: "doctor_name",         label: "Doctor Name",           w: 70, h: 8, font: 11, source: "doctor.name" },
    { type: "doctor_qualification",label: "Doctor Qualification",  w: 70, h: 7, font: 10, source: "doctor.qualification" },
    { type: "doctor_registration", label: "Reg. No.",              w: 40, h: 7, font: 10, source: "doctor.registration" },
    { type: "signature",           label: "Doctor Signature",      w: 50, h: 20, font: 10, source: "doctor.signature" },
  ]},
  { group: "Clinic",    icon: Building2, items: [
    { type: "clinic_name",         label: "Clinic Name",     w: 90, h: 8, font: 12, source: "clinic.name" },
    { type: "clinic_address",      label: "Clinic Address",  w: 90, h: 10, font: 10, source: "clinic.address" },
    { type: "clinic_phone",        label: "Clinic Phone",    w: 50, h: 7, font: 10, source: "clinic.phone" },
  ]},
  { group: "Clinical", icon: ClipboardList, items: [
    { type: "chief_complaint",     label: "Chief Complaint", w: 170, h: 12, font: 10, source: "rx.chief_complaint" },
    { type: "diagnosis",           label: "Diagnosis",       w: 170, h: 12, font: 10, source: "rx.diagnosis" },
    { type: "treatment",           label: "Treatment",       w: 170, h: 12, font: 10, source: "rx.treatment" },
    { type: "prescription",        label: "Prescription",    w: 170, h: 40, font: 10, source: "rx.medicines" },
    { type: "advice",              label: "Advice",          w: 170, h: 15, font: 10, source: "rx.advice" },
    { type: "clinical_notes",      label: "Clinical Notes",  w: 170, h: 20, font: 10, source: "rx.notes" },
    // Structured card that renders a mini Date · Time · Doctor · Purpose
    // grid at PDF-render time. Data pulled from rx.next_appointment.
    { type: "next_appointment",    label: "Next Appointment", w: 90, h: 22, font: 9.5, source: "", structured: true },
  ]},
  { group: "Structure", icon: Type, items: [
    { type: "short_text",          label: "Short Text",      w: 60, h: 7,  font: 10, source: null },
    { type: "long_text",           label: "Long Text",       w: 170, h: 20, font: 10, source: null },
    { type: "checkbox_group",      label: "Checkbox Group",  w: 170, h: 10, font: 10, source: null,
       config: { options: ["Diabetes", "Cardiac Disease", "Thyroid", "Pregnancy", "Blood Disorder", "HIV/Hepatitis", "Asthma", "Allergy", "None"], multi: true } },
    { type: "table",               label: "Table",           w: 170, h: 40, font: 9, source: null,
       config: { columns: ["Medicine", "Dose", "Frequency", "Duration"] } },
    { type: "image",               label: "Image",           w: 60, h: 40, font: 10, source: null },
    { type: "custom",              label: "Custom Field",    w: 60, h: 7, font: 10, source: null },
  ]},
];
const CATALOG_ICON = {
  patient_name: User, patient_id: User, patient_age_gender: User,
  patient_phone: User, patient_dob: User, patient_address: User,
  patient_blood_group: User, current_date: Calendar, appointment_date: Calendar,
  appointment_time: Calendar, follow_up_date: Calendar, doctor_name: Stethoscope,
  doctor_qualification: Stethoscope, doctor_registration: Stethoscope,
  signature: PenTool, clinic_name: Building2, clinic_address: Building2,
  clinic_phone: Building2, chief_complaint: ClipboardList,
  diagnosis: ClipboardList, treatment: ClipboardList, prescription: ClipboardList,
  advice: ClipboardList, clinical_notes: ClipboardList, next_appointment: Calendar,
  short_text: Type,
  long_text: Type, checkbox_group: CheckSquare, table: TableIcon, image: ImageIcon,
  custom: Type,
};

// ---------------------------------------------------------------------
// Preview values shown when the operator toggles "Preview".
// ---------------------------------------------------------------------
const SAMPLE = {
  patient_name: "Raj Sharma",
  patient_id: "P00234",
  patient_age_gender: "32 / M",
  patient_phone: "+91 98111 22334",
  patient_dob: "1994-03-14",
  patient_address: "12 MG Road, Bengaluru",
  patient_blood_group: "O+",
  current_date: new Date().toLocaleDateString(),
  appointment_date: "11 Aug 2026",
  appointment_time: "10:30 AM",
  follow_up_date: "25 Aug 2026",
  doctor_name: "Dr. Atul Parashar",
  doctor_qualification: "BDS, MDS (Ortho)",
  doctor_registration: "MHDC-12345",
  clinic_name: "Dent-O-Sculpt Dental Clinic",
  clinic_address: "3rd Floor, Pearl Plaza, Sector 12, Gurugram",
  clinic_phone: "+91 76782 46763",
  chief_complaint: "Pain in lower right molar for 3 days",
  diagnosis: "Chronic irreversible pulpitis, 46",
  treatment: "Root Canal Treatment (46) — 3 visits",
  prescription: "Amoxicillin 500 mg  1-1-1  x 5 days\nIbuprofen 400 mg  1-0-1  x 3 days",
  advice: "Avoid chewing on right side. Warm saline rinse 3× daily.",
  clinical_notes: "OPG shows periapical radiolucency at 46 apex.",
  next_appointment: "Next Appointment\nDate: 25 Aug 2026 · Time: 10:30\nDr. Amelia Chen · Follow-up",
};

const uid = () => "f_" + Math.random().toString(36).slice(2, 10);

export default function LetterheadEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const analysisRequested = search.get("analysis") === "1";
  const [lh, setLh]       = useState(null);
  const [fields, setFields] = useState([]);
  const [analysisMeta, setAnalysisMeta] = useState(null);
  const [selId, setSelId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom]   = useState(2.4);   // px per mm
  const [preview, setPreview] = useState(false);

  const canvasRef = useRef();
  const dragRef   = useRef(null);        // {mode:'move'|'resize', fid, dx, dy, startX, startY, startW, startH}

  // ─── Load letterhead
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/letterheads/${id}`);
        setLh(data);
        // If the operator arrived here via "Auto-analyze", prefer the fresh
        // vision-proposal fields over whatever was previously saved so the
        // review is against the latest AI output. The proposal isn't
        // persisted until they hit Save.
        if (analysisRequested) {
          try {
            const raw = sessionStorage.getItem(`lh_analysis_${id}`);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed?.fields) && parsed.fields.length) {
                setFields(parsed.fields);
                setAnalysisMeta({ model: parsed.model, at: parsed.at, count: parsed.fields.length });
                setDirty(true);
                sessionStorage.removeItem(`lh_analysis_${id}`);
                toast.success(`Vision AI detected ${parsed.fields.length} fields — review and save`);
                return;
              }
            }
          } catch { /* ignore */ }
        }
        setFields(data.fields || []);
      } catch (e) { toast.error(apiError(e, "Failed to load letterhead")); navigate("/app/settings/documents/letterheads"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, analysisRequested]);

  const geo = lh?.geometry || {};
  const pageW = geo.page_width_mm  || 210;
  const pageH = geo.page_height_mm || 297;
  const canvasW = pageW * zoom;
  const canvasH = pageH * zoom;
  const selField = fields.find(f => f.id === selId) || null;

  // ─── Add field from library (drop at rough center of visible canvas)
  const addField = (item) => {
    const f = {
      id: uid(),
      type: item.type,
      label: item.label,
      data_source: item.source || null,
      x_mm: Math.max(10, pageW / 2 - item.w / 2),
      y_mm: Math.max(10, pageH / 4),
      w_mm: item.w, h_mm: item.h,
      page: 1,
      font_family: "Helvetica",
      font_size_pt: item.font,
      bold: false, italic: false, underline: false,
      align: "left",
      color: "#111318",
      line_height: 1.3,
      required: false, editable: true, auto_fill: !!item.source,
      config: item.config || {},
    };
    setFields((xs) => [...xs, f]);
    setSelId(f.id);
    setDirty(true);
  };

  const updateSel = (patch) => {
    if (!selId) return;
    setFields((xs) => xs.map((f) => f.id === selId ? { ...f, ...patch } : f));
    setDirty(true);
  };
  const removeField = (fid) => {
    setFields((xs) => xs.filter((f) => f.id !== fid));
    if (selId === fid) setSelId(null);
    setDirty(true);
  };
  const duplicateField = (fid) => {
    const src = fields.find(f => f.id === fid); if (!src) return;
    const copy = { ...src, id: uid(), x_mm: src.x_mm + 5, y_mm: src.y_mm + 5 };
    setFields((xs) => [...xs, copy]);
    setSelId(copy.id);
    setDirty(true);
  };

  // ─── Drag & resize
  const onMouseDownField = (e, fid, mode = "move") => {
    e.stopPropagation();
    const f = fields.find(x => x.id === fid); if (!f) return;
    setSelId(fid);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      mode, fid,
      startClientX: e.clientX, startClientY: e.clientY,
      startX: f.x_mm, startY: f.y_mm, startW: f.w_mm, startH: f.h_mm,
      canvasLeft: rect.left, canvasTop: rect.top,
    };
    document.addEventListener("mousemove", onDragMove);
    document.addEventListener("mouseup",   onDragEnd);
  };
  const onDragMove = (e) => {
    const d = dragRef.current; if (!d) return;
    const dxMm = (e.clientX - d.startClientX) / zoom;
    const dyMm = (e.clientY - d.startClientY) / zoom;
    setFields((xs) => xs.map((f) => {
      if (f.id !== d.fid) return f;
      if (d.mode === "move") {
        const nx = clamp(d.startX + dxMm, 0, pageW - f.w_mm);
        const ny = clamp(d.startY + dyMm, 0, pageH - f.h_mm);
        return { ...f, x_mm: round(nx), y_mm: round(ny) };
      }
      const nw = clamp(d.startW + dxMm, 8,  pageW - f.x_mm);
      const nh = clamp(d.startH + dyMm, 5,  pageH - f.y_mm);
      return { ...f, w_mm: round(nw), h_mm: round(nh) };
    }));
    setDirty(true);
  };
  const onDragEnd = () => {
    dragRef.current = null;
    document.removeEventListener("mousemove", onDragMove);
    document.removeEventListener("mouseup",   onDragEnd);
  };

  // ─── Save
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/letterheads/${id}/fields`, { fields });
      toast.success(`Saved ${fields.length} field(s)`);
      setDirty(false);
      if (data) setLh(data);
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  // ─── Accept & Activate — makes this the one active letterhead used for
  // custom_letterhead Rx PDFs. Guarded server-side: fields must not be empty.
  const acceptActivate = async () => {
    if (dirty) {
      if (!window.confirm("You have unsaved changes. Save first?")) return;
      await save();
    }
    try {
      const { data } = await api.post(`/letterheads/${id}/activate`);
      toast.success("Letterhead activated — Custom Letterhead is now available on prescriptions");
      if (data) setLh(data);
    } catch (e) { toast.error(apiError(e, "Could not activate")); }
  };

  // ─── Keyboard: arrows nudge, Del removes
  useEffect(() => {
    const onKey = (e) => {
      if (!selId) return;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      const step = e.shiftKey ? 5 : 1;
      const map = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (map[e.key]) {
        e.preventDefault();
        const f = fields.find(x => x.id === selId); if (!f) return;
        const [dx, dy] = map[e.key];
        updateSel({
          x_mm: clamp(f.x_mm + dx, 0, pageW - f.w_mm),
          y_mm: clamp(f.y_mm + dy, 0, pageH - f.h_mm),
        });
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault(); removeField(selId);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, fields, pageW, pageH]);

  if (!lh) {
    return <div className="p-10 text-center text-sm text-ink-mid">
      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading letterhead…
    </div>;
  }

  return (
    <div className="min-h-screen bg-linen flex flex-col" data-testid="lh-editor-page">
      {/* Analysis banner */}
      {analysisMeta && dirty && (
        <div className="bg-violet-50 border-b border-violet-200 px-4 py-2.5 flex items-center gap-3 text-xs"
              data-testid="lh-analysis-banner">
          <span className="inline-flex items-center gap-1.5 text-violet-800 font-medium">
            ✨ Vision AI detected {analysisMeta.count} fields
          </span>
          <span className="text-violet-600">Review below · Hover a field to see AI reasoning · Save when happy</span>
          <span className="ml-auto text-violet-500">{analysisMeta.model}</span>
        </div>
      )}
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-[#E7E1D3]">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/app/settings/documents/letterheads"
                 className="text-ink-mid hover:text-ink"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0">
            <div className="text-xs text-ink-mid">Letterhead editor</div>
            <div className="text-sm font-medium text-ink truncate" data-testid="lh-editor-name">{lh.name}</div>
          </div>
          <span className="text-[10px] uppercase tracking-wider bg-linen border border-[#E7E1D3] rounded-full px-2 py-0.5 text-ink-mid">
            {geo.detected_page_size} · {geo.orientation} · {pageW}×{pageH}mm
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-linen rounded-full px-2 py-1 border border-[#E7E1D3]">
            <button onClick={() => setZoom(z => Math.max(1.0, +(z - 0.4).toFixed(1)))} className="p-1 text-ink-mid hover:text-ink" data-testid="lh-zoom-out"><ZoomOut className="w-3.5 h-3.5" /></button>
            <div className="text-xs text-ink w-14 text-center" data-testid="lh-zoom-value">{Math.round(zoom * 100 / 3.78)}%</div>
            <button onClick={() => setZoom(z => Math.min(5.0, +(z + 0.4).toFixed(1)))} className="p-1 text-ink-mid hover:text-ink" data-testid="lh-zoom-in"><ZoomIn className="w-3.5 h-3.5" /></button>
            <button onClick={() => setZoom(2.4)} className="p-1 text-ink-mid hover:text-ink" title="Reset zoom"><RotateCcw className="w-3.5 h-3.5" /></button>
          </div>
          <button onClick={() => setPreview(v => !v)}
                   className={`inline-flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-full transition-colors ${preview ? "text-white bg-ink" : "text-ink bg-linen border border-[#E7E1D3] hover:border-ink/30"}`}
                   data-testid="lh-preview-toggle">
            {preview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {preview ? "Editing" : "Preview"}
          </button>
          <button onClick={save} disabled={saving || !dirty}
                   className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors disabled:opacity-40"
                   data-testid="lh-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button onClick={acceptActivate}
                   disabled={fields.length === 0}
                   className={`inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-full transition-colors disabled:opacity-40 ${lh?.status === "active" ? "bg-emerald-50 text-emerald-800 border border-emerald-300" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                   data-testid="lh-activate-btn"
                   title="Set this letterhead as the clinic's active custom template">
            <CheckCircle2 className="w-4 h-4" />
            {lh?.status === "active" ? "Active" : "Accept & Activate"}
          </button>
        </div>
      </div>

      {/* 3-column workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Field library */}
        <aside className="w-64 shrink-0 bg-white border-r border-[#E7E1D3] overflow-y-auto" data-testid="lh-field-library">
          <div className="p-3 text-xs uppercase tracking-wider text-ink-mid border-b border-[#E7E1D3]">Field library</div>
          {CATALOG.map((grp) => (
            <div key={grp.group} className="border-b border-[#E7E1D3]">
              <div className="px-3 py-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-mid bg-linen/40">
                <grp.icon className="w-3.5 h-3.5" /> {grp.group}
              </div>
              {grp.items.map((it) => (
                <button key={it.type} onClick={() => addField(it)}
                         className="w-full flex items-center gap-2 px-3 py-2 text-xs text-ink hover:bg-linen transition-colors text-left"
                         data-testid={`lh-add-${it.type}`}>
                  <Plus className="w-3 h-3 text-ink-mid shrink-0" />
                  <span className="truncate">{it.label}</span>
                </button>
              ))}
            </div>
          ))}
        </aside>

        {/* Canvas */}
        <main className="flex-1 overflow-auto p-6 flex items-start justify-center" data-testid="lh-canvas-scroll">
          <div className="shadow-xl bg-white" style={{ width: canvasW, height: canvasH }}>
            <div
              ref={canvasRef}
              onClick={() => setSelId(null)}
              className="relative overflow-hidden select-none"
              style={{ width: canvasW, height: canvasH }}
              data-testid="lh-canvas"
            >
              {/* Background: uploaded letterhead. Never re-rendered — Viyora places it as-is. */}
              {lh.asset?.ext === "pdf" ? (
                <iframe
                  src={`${backendBase()}${lh.asset_url || `/api/public/letterhead-asset/${id}`}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                  title={lh.name}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  data-testid="lh-canvas-bg-pdf"
                />
              ) : (
                <img
                  src={`${backendBase()}${lh.asset_url || `/api/public/letterhead-asset/${id}`}`}
                  alt={lh.name}
                  className="absolute inset-0 w-full h-full object-fill pointer-events-none"
                  data-testid="lh-canvas-bg-img"
                />
              )}

              {/* Field overlays */}
              {fields.map((f) => (
                <FieldBox
                  key={f.id} f={f} zoom={zoom} preview={preview}
                  selected={f.id === selId}
                  onMouseDown={(e, mode) => onMouseDownField(e, f.id, mode)}
                  onDuplicate={() => duplicateField(f.id)}
                  onDelete={() => removeField(f.id)}
                />
              ))}
            </div>
          </div>
        </main>

        {/* Properties panel */}
        <aside className="w-72 shrink-0 bg-white border-l border-[#E7E1D3] overflow-y-auto" data-testid="lh-properties">
          <div className="p-3 text-xs uppercase tracking-wider text-ink-mid border-b border-[#E7E1D3] flex items-center justify-between">
            <span>Properties</span>
            <span className="text-[10px] text-ink-mid" data-testid="lh-fields-count">{fields.length} field(s)</span>
          </div>
          {selField ? (
            <PropertiesPanel f={selField} onChange={updateSel} onDelete={() => removeField(selField.id)} />
          ) : (
            <div className="p-6 text-xs text-ink-mid text-center">
              Select a field on the canvas, or add one from the library on the left.
              <div className="mt-3 space-y-1 text-[10px] text-ink-mid text-left">
                <div>◇ Drag to move · corner to resize</div>
                <div>◇ ← → ↑ ↓  nudge (shift = 5 mm)</div>
                <div>◇ Del / Backspace  remove</div>
                <div>◇ Preview toggle shows sample data</div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- field box --- */
function FieldBox({ f, zoom, preview, selected, onMouseDown, onDuplicate, onDelete }) {
  const Icon = CATALOG_ICON[f.type] || Type;
  const style = {
    left:   f.x_mm * zoom,
    top:    f.y_mm * zoom,
    width:  f.w_mm * zoom,
    height: f.h_mm * zoom,
    fontSize: f.font_size_pt * zoom / 2.834,   // pt → px (1pt = 1/72 inch = 0.3528 mm)
    fontWeight: f.bold ? 700 : 400,
    fontStyle:  f.italic ? "italic" : "normal",
    textDecoration: f.underline ? "underline" : "none",
    textAlign:  f.align,
    color:      f.color || "#111318",
    lineHeight: f.line_height || 1.3,
  };
  const label = preview
    ? (SAMPLE[f.type] || (f.type === "signature" ? "[signature]" : f.label))
    : f.label;

  return (
    <div
      style={style}
      onMouseDown={(e) => onMouseDown(e, "move")}
      onClick={(e) => e.stopPropagation()}
      className={`absolute cursor-move overflow-hidden whitespace-pre-line px-1 py-0.5 rounded-sm transition-colors
                    ${preview
                       ? ""
                       : (selected
                             ? "outline outline-2 outline-ink bg-white/70"
                             : "outline outline-1 outline-dashed outline-ink/40 bg-white/40 hover:bg-white/70")}`}
      data-testid={`lh-field-${f.type}-${f.id}`}
    >
      {!preview && (
        <div className="absolute -top-4 left-0 text-[9px] uppercase tracking-wider text-ink-mid flex items-center gap-1 pointer-events-none">
          <Icon className="w-2.5 h-2.5" /> {f.label}
          {typeof f.confidence === "number" && (
            <span
              className={`ml-1 px-1 rounded text-white text-[8px] ${f.confidence >= 0.85 ? "bg-emerald-600" : f.confidence >= 0.65 ? "bg-amber-500" : "bg-red-500"}`}
              title={f.reasoning || "AI confidence"}
              data-testid={`lh-field-conf-${f.id}`}
            >
              {Math.round(f.confidence * 100)}%
            </span>
          )}
        </div>
      )}
      {label}
      {!preview && selected && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            className="absolute -top-4 right-6 p-0.5 text-ink-mid hover:text-ink bg-white rounded"
            data-testid={`lh-field-dup-${f.id}`}><Copy className="w-2.5 h-2.5" /></button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="absolute -top-4 right-0 p-0.5 text-red-600 hover:text-red-700 bg-white rounded"
            data-testid={`lh-field-del-${f.id}`}><Trash2 className="w-2.5 h-2.5" /></button>
          <div
            onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, "resize"); }}
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-ink border border-white cursor-nwse-resize"
            data-testid={`lh-field-resize-${f.id}`}
          />
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------- properties -- */
function PropertiesPanel({ f, onChange, onDelete }) {
  return (
    <div className="p-4 space-y-3 text-sm">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-ink-mid">{f.type}</div>
        <button onClick={onDelete} className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                 data-testid="lh-prop-delete">
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>

      <Field label="Label">
        <input value={f.label || ""} onChange={(e) => onChange({ label: e.target.value })}
                className={INPUT} data-testid="lh-prop-label" />
      </Field>

      <Field label="Data source">
        <input value={f.data_source || ""} onChange={(e) => onChange({ data_source: e.target.value })}
                placeholder="e.g. patient.name  or  rx.diagnosis"
                className={INPUT} data-testid="lh-prop-source" />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="X (mm)">
          <input type="number" value={f.x_mm} onChange={(e) => onChange({ x_mm: +e.target.value || 0 })} className={INPUT} data-testid="lh-prop-x" />
        </Field>
        <Field label="Y (mm)">
          <input type="number" value={f.y_mm} onChange={(e) => onChange({ y_mm: +e.target.value || 0 })} className={INPUT} data-testid="lh-prop-y" />
        </Field>
        <Field label="W (mm)">
          <input type="number" value={f.w_mm} onChange={(e) => onChange({ w_mm: Math.max(5, +e.target.value || 0) })} className={INPUT} data-testid="lh-prop-w" />
        </Field>
        <Field label="H (mm)">
          <input type="number" value={f.h_mm} onChange={(e) => onChange({ h_mm: Math.max(3, +e.target.value || 0) })} className={INPUT} data-testid="lh-prop-h" />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Font size (pt)">
          <input type="number" value={f.font_size_pt} onChange={(e) => onChange({ font_size_pt: +e.target.value || 10 })} className={INPUT} data-testid="lh-prop-font-size" />
        </Field>
        <Field label="Align">
          <select value={f.align} onChange={(e) => onChange({ align: e.target.value })} className={INPUT} data-testid="lh-prop-align">
            <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
          </select>
        </Field>
      </div>

      <div className="flex flex-wrap gap-1">
        {[["bold", "B"], ["italic", "I"], ["underline", "U"]].map(([k, l]) => (
          <button key={k}
                   onClick={() => onChange({ [k]: !f[k] })}
                   className={`px-3 py-1.5 rounded-full text-xs font-medium border ${f[k] ? "bg-ink text-white border-ink" : "bg-white text-ink border-[#E7E1D3]"}`}
                   data-testid={`lh-prop-${k}`}>{l}</button>
        ))}
      </div>

      <Field label="Color">
        <input type="color" value={f.color || "#111318"} onChange={(e) => onChange({ color: e.target.value })}
                className="h-9 w-full rounded-full border border-[#E7E1D3] cursor-pointer" data-testid="lh-prop-color" />
      </Field>

      <div className="pt-2 border-t border-[#E7E1D3] space-y-2">
        <label className="flex items-center gap-2 text-xs text-ink">
          <input type="checkbox" checked={!!f.editable} onChange={(e) => onChange({ editable: e.target.checked })}
                  data-testid="lh-prop-editable" />
          Editable at document time
        </label>
        <label className="flex items-center gap-2 text-xs text-ink">
          <input type="checkbox" checked={!!f.auto_fill} onChange={(e) => onChange({ auto_fill: e.target.checked })}
                  data-testid="lh-prop-autofill" />
          Auto-fill from data source
        </label>
        <label className="flex items-center gap-2 text-xs text-ink">
          <input type="checkbox" checked={!!f.required} onChange={(e) => onChange({ required: e.target.checked })}
                  data-testid="lh-prop-required" />
          Required
        </label>
      </div>

      {f.type === "checkbox_group" && (
        <CheckboxGroupEditor value={f.config?.options || []}
                              onChange={(options) => onChange({ config: { ...(f.config || {}), options } })} />
      )}
      {f.type === "table" && (
        <TableColumnsEditor value={f.config?.columns || []}
                              onChange={(columns) => onChange({ config: { ...(f.config || {}), columns } })} />
      )}
    </div>
  );
}

function CheckboxGroupEditor({ value, onChange }) {
  const [tmp, setTmp] = useState("");
  return (
    <div className="pt-2 border-t border-[#E7E1D3]">
      <div className="text-xs uppercase tracking-wider text-ink-mid mb-1.5">Options</div>
      <div className="space-y-1">
        {value.map((opt, i) => (
          <div key={`${opt}-${i}`} className="flex items-center gap-1">
            <input value={opt} onChange={(e) => onChange(value.map((v, j) => j === i ? e.target.value : v))}
                    className={INPUT} data-testid={`lh-prop-cb-opt-${i}`} />
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-1 text-red-600" data-testid={`lh-prop-cb-del-${i}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-1">
          <input value={tmp} onChange={(e) => setTmp(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && tmp.trim()) { onChange([...value, tmp.trim()]); setTmp(""); } }}
                  placeholder="Add option and press Enter"
                  className={INPUT} data-testid="lh-prop-cb-new" />
        </div>
      </div>
    </div>
  );
}

function TableColumnsEditor({ value, onChange }) {
  return (
    <div className="pt-2 border-t border-[#E7E1D3]">
      <div className="text-xs uppercase tracking-wider text-ink-mid mb-1.5">Table columns</div>
      <div className="space-y-1">
        {value.map((c, i) => (
          <div key={`${c}-${i}`} className="flex items-center gap-1">
            <input value={c} onChange={(e) => onChange(value.map((v, j) => j === i ? e.target.value : v))} className={INPUT} data-testid={`lh-prop-tc-${i}`} />
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-1 text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button onClick={() => onChange([...value, "Column"])}
                 className="text-xs text-ink-mid hover:text-ink inline-flex items-center gap-1"
                 data-testid="lh-prop-tc-add">
          <Plus className="w-3 h-3" /> Add column
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-ink-mid mb-1">{label}</div>
      {children}
    </label>
  );
}

const INPUT = "w-full bg-white border border-[#E7E1D3] rounded-full px-3 py-1.5 text-xs text-ink focus:outline-none focus:border-ink";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const round = (v) => Math.round(v * 10) / 10;
